/**
 * useServersVerdict — what the shared servers list says about the organization, decided once
 * per mount. ServerSetupGate and SetupServerPage both decide from it.
 *
 * The list is the servers atom's. It may hold an empty list from before the caller mounted,
 * for example just after the gate sent the user to setup. The atom loads on mount, after the
 * first render, so only a load seen in flight since mount may decide.
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { authSession } from '@/auth/authSession';
import { switchActiveOrg } from '@/auth/orgScope';
import { GateStatus } from '@/types/servers';

import { getNextGateStatus, getServersFailureStatus, getServersStatus } from './serversVerdict';
import { useServers } from './useServers';

interface ServersVerdictResult {
    status: GateStatus;
    /** Loads the list again and waits for that load to decide. */
    retry: () => void;
}

export function useServersVerdict(): ServersVerdictResult {
    const {
        servers, error, isFetching, revalidate, refetch,
    } = useServers();
    // An error must NOT fall through to `None`: a transient backend blip (e.g. a service being
    // redeployed) would bounce a fully-provisioned org onto the first-server pairing wizard.
    const [status, setStatus] = useState(GateStatus.Awaiting);

    const retry = (): void => {
        setStatus(GateStatus.Awaiting);
        refetch();
    };

    // Each caller sits on its own route, so its mount is what mounts the atom, and that mount
    // starts the load that the caller waits for. A reader above the caller could leave the atom
    // freshly settled, and the spinner would stay up.
    useEffect(() => {
        revalidate();
    }, [revalidate]);

    useEffect(() => {
        // A load that succeeds clears `error`, so one present when it settles is its failure.
        const verdict = error ? getServersFailureStatus(error) : getServersStatus(servers);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- follows the atom's load
        setStatus(getNextGateStatus(status, isFetching, verdict));
    }, [status, isFetching, error, servers]);

    // 403 recovery: the stored org / session pair is known-bad — retrying would loop forever.
    // Drop the stale org and the whole session; AuthGate then bounces to /login, and a fresh
    // sign-in re-mints the token with the account's current memberships.
    useEffect(() => {
        if (status !== GateStatus.Forbidden) {
            return;
        }
        toast.error(
            'Your session has no access to the selected organization. Please sign in again.',
        );
        switchActiveOrg(null);
        authSession.logout();
    }, [status]);

    return { status, retry };
}
